import { MemberExperienceUserFromIdToken } from '@guildeducationinc/auth-frontend-tools';
import { isBot } from 'src/utilities/botUtility';
import { AlgoliaUiStateType } from '@src/clients/Algolia/constants';
import { InsightsMethodMap } from 'instantsearch.js/es/types';
import { ALGOLIA_API_KEY, ALGOLIA_APPLICATION_ID, SEGMENT_KEY } from '../utilities/envConfig';

interface SegmentProperties {
	abTestId?: number;
	abTestVariantId?: number;
	careerId?: string;
	catalogCardLocation?: string;
	city?: string;
	differentAttributes?: Array<{
		attributeData: string[];
		attributeLabel: string;
	}>;
	employerUuid?: string;
	entityId?: string;
	experimentId?: string;
	experimentName?: string;
	filters?: string;
	filterCategory?: string;
	filterValue?: string[];
	fromPath?: string;
	id?: string;
	indexUsed?: string;
	institutionID?: string;
	location?: Location | string;
	loggedIn?: boolean;
	navigationMethod?: string; // click or scrollTo for Program Overview Stepper
	nbHits?: number;
	nonInteraction?: number;
	objectId?: string;
	objectIds?: string[];
	offeringID?: string;
	offeringIDs?: string;
	offeringLocation?: string;
	optimizelyUserId?: string;
	page?: string;
	path?: string;
	product?: string;
	programId?: string;
	programIds?: string[];
	questionKey?: string;
	questionType?: string;
	responseKey?: string | string[]; // this is both string and string[]
	sameAttributes?: Array<{
		attributeData: string[];
		attributeLabel: string;
	}>;
	searchState?: { pageNumber?: number; sourceId?: string };
	state?: string;
	tag?: string;
	text?: string;
	type?: string;
	typedTerm?: string;
	toPath?: string;
	url?: string;
	variationId?: string;
	variationName?: string;
	video?: string;
	userRoles?: string[];
}

interface EventProperties {
	facetFilters?: Array<string[]>;
	filters?: string[];
	index?: string;
	objectId?: string;
	objectIds?: string[];
	programId?: string;
	programIds?: string[];
	position?: number;
	queryId?: string;
	segmentEventName?: string;
	segmentProperties?: SegmentProperties;
	uiState?: AlgoliaUiStateType;
	user?: MemberExperienceUserFromIdToken;
	userUuid?: string;
}

interface UserProperties {
	createdAt?: string;
	email: string;
	employerId: string;
	firstName?: string;
	lastName?: string;
}
// There is no business logic in this file, we're wrapping functions provided by the
// Segment library for the sake of providing types and easier mocking.
// https://segment.com/docs/sources/website/analytics.js/quickstart/

export const trackPage = (location: string, options?: SegmentProperties): void => {
	if (!window.analytics) {
		return;
	}
	if (isBot()) {
		return;
	}
	window.analytics.page(location, options);
};

export const defaultAnalyticsProperties = {
	loggedIn: true,
	product: 'catalog',
};

export const trackEvent = (name: string, properties: SegmentProperties): void => {
	properties = {
		product: properties.product || 'catalog',
		typedTerm: properties.typedTerm || '',
		...properties,
		...defaultAnalyticsProperties,
	};

	if (!window.analytics) {
		return;
	}
	if (isBot()) {
		return;
	}

	window.analytics.track(name, properties);
};

export const algoliaEvent = (
	name: keyof InsightsMethodMap,
	props: {
		eventName: string;
		filters?: string[];
		index: string;
		positions?: number[];
		objectIDs?: string[];
		queryID: string;
		userToken?: string;
		userUuid?: string;
	}
) => {
	if (isBot()) {
		return;
	}
	window.aa(name, {
		userToken: props.userUuid,
		index: props.index,
		...props,
	});
};

export const identifyUser = (uuid: string, options?: UserProperties): void => {
	if (!window.analytics) {
		return;
	}
	if (isBot()) {
		return;
	}
	window.analytics.identify(uuid, {
		product: 'catalog',
		...options,
	});
};

export const resetSegment = () => {
	if (!window.analytics) {
		return;
	}
	window.analytics.reset();
};

export const loadSegment = () => {
	if (window.analytics && window.analytics.load && SEGMENT_KEY) {
		window.analytics.load(SEGMENT_KEY);
	}
};

export const loadAlgoliaAnalytics = () => {
	if (window.aa && ALGOLIA_API_KEY && ALGOLIA_APPLICATION_ID) {
		window.aa('init', {
			appId: ALGOLIA_APPLICATION_ID,
			apiKey: ALGOLIA_API_KEY,
		});
	}
};

export const getAnonymousId = (): string => {
	if (!window.analytics || !window.analytics.user) {
		return;
	}

	return window.analytics.user().anonymousId();
};

export const trackViewedObjects = (eventProperties: EventProperties): void => {
	if (!window.aa) {
		return;
	}

	const { objectIds, userUuid, index, queryId } = eventProperties;

	if (!userUuid) {
		return;
	}

	if (objectIds.length > 0) {
		algoliaEvent('viewedObjectIDs', {
			eventName: 'viewedObjectIDs',
			index,
			objectIDs: objectIds,
			queryID: queryId,
			userToken: userUuid,
		});
	}
};
export const trackViewedFilters = (eventProperties: EventProperties): void => {
	const { uiState, userUuid, index, queryId } = eventProperties;

	if (!userUuid || !window.aa) {
		return;
	}

	const menus = uiState.menu;
	const menuFilters = menus
		? Object.keys(menus)
				.map(key => {
					if (typeof menus[key] === 'string') {
						return `${key}:${menus[key]}`;
					}
					return null;
				})
				.filter(menuValues => menuValues !== null)
		: [];

	const refinements = uiState.refinementList;
	const refinementFilters = refinements
		? Object.keys(refinements)
				.map(key => {
					let filterValue = '';
					if (refinements[key] && refinements[key].length >= 2) {
						refinements[key].forEach(value => filterValue.concat(',', `${key}:${value}}`));
					}
					filterValue = `${key}:${refinements[key]}`;
					return filterValue;
				})
				.filter(filterValues => !filterValues.includes(':null'))
				.filter(finalValues => finalValues.split(':')[1].length > 0)
		: [];

	const filters = [...menuFilters, ...refinementFilters];

	if (filters.length > 0 && filters.length <= 10) {
		algoliaEvent('viewedFilters', {
			eventName: 'viewedFilters',
			filters,
			index,
			queryID: queryId,
			userToken: userUuid,
		});
	}
};

export const trackProgramClick = (eventProperties: EventProperties): void => {
	const { index, objectId, position, queryId, userUuid } = eventProperties;

	if (!window.aa) {
		return;
	}
	algoliaEvent('clickedObjectIDsAfterSearch', {
		eventName: 'offering_click_rate',
		index,
		objectIDs: [objectId],
		positions: [position],
		queryID: queryId,
		userToken: userUuid,
	});
};

export const trackBookmarkSaved = (eventProperties: EventProperties): void => {
	const { segmentProperties } = eventProperties;

	trackEvent('BookmarkSaved', segmentProperties);
};

export const trackBookmarkRemoved = (eventProperties: EventProperties): void => {
	const { segmentProperties } = eventProperties;

	trackEvent('BookmarkRemoved', segmentProperties);
};

export const trackConversion = (eventProperties: EventProperties): void => {
	const { objectId, programId, segmentEventName, userUuid, queryId, index } = eventProperties;

	trackEvent(segmentEventName, { programId });

	if (!window.aa) {
		return;
	}

	algoliaEvent('convertedObjectIDsAfterSearch', {
		eventName: 'application_start_rate',
		index,
		queryID: queryId,
		objectIDs: [objectId],
		userToken: userUuid,
	});
};

export const trackClickedFilters = (eventProperties: EventProperties): void => {
	if (!window.aa) {
		return;
	}
	const { filters, index, queryId, userUuid } = eventProperties;

	if (filters.length === 0) {
		return;
	}

	if (filters.length <= 10) {
		algoliaEvent('clickedFilters', {
			eventName: 'clickedFilters',
			filters,
			index,
			queryID: queryId,
			userToken: userUuid,
		});
	}
};
